'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import AppLogo from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';

interface LoginFormValues {
  email: string;
  password: string;
}

export default function LoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormValues>();

  const onSubmit = async (data: LoginFormValues) => {
    setLoading(true);
    try {
      const supabase = createClient();
      const { error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });
      if (error) {
        toast.error(error.message || 'Invalid credentials — please check your email and password');
        setLoading(false);
        return;
      }
      toast.success('Welcome back, Administrator');
      router.push('/');
      router.refresh();
    } catch (err: any) {
      toast.error(err?.message || 'Sign in failed — please try again');
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md space-y-6 fade-in">
      {/* Mobile logo */}
      <div className="lg:hidden flex items-center gap-3 mb-2">
        <AppLogo size={36} />
        <div>
          <p className="text-sm font-700 gold-gradient-text" style={{ fontWeight: 700 }}>
            YOUNG ARTIST ACADEMY
          </p>
          <p className="text-xs text-muted-foreground">Admin Portal</p>
        </div>
      </div>

      <div>
        <h1 className="text-2xl font-700 text-foreground mb-1" style={{ fontWeight: 700 }}>
          Sign In
        </h1>
        <p className="text-sm text-muted-foreground">
          Admin access only · Single authorized account
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Email */}
        <div className="space-y-1.5">
          <label className="block text-sm font-500 text-foreground" style={{ fontWeight: 500 }}>
            Email Address
          </label>
          <div className="relative">
            <Icon name="Mail" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="email"
              placeholder="admin@youngartistacademy.info"
              {...register('email', {
                required: 'Email is required',
                pattern: { value: /^\S+@\S+$/i, message: 'Enter a valid email address' },
              })}
              className={`input-dark w-full pl-9 pr-4 py-2.5 text-sm ${errors.email ? 'input-dark-error' : ''}`}
            />
          </div>
          {errors.email && (
            <p className="text-xs text-error flex items-center gap-1">
              <Icon name="AlertCircle" size={12} />
              {errors.email.message}
            </p>
          )}
        </div>

        {/* Password */}
        <div className="space-y-1.5">
          <label className="block text-sm font-500 text-foreground" style={{ fontWeight: 500 }}>
            Password
          </label>
          <div className="relative">
            <Icon name="Lock" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              {...register('password', { required: 'Password is required', minLength: { value: 6, message: 'Password must be at least 6 characters' } })}
              className={`input-dark w-full pl-9 pr-10 py-2.5 text-sm ${errors.password ? 'input-dark-error' : ''}`}
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
            </button>
          </div>
          {errors.password && (
            <p className="text-xs text-error flex items-center gap-1">
              <Icon name="AlertCircle" size={12} />
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="btn-gold w-full py-2.5 rounded-lg text-sm flex items-center justify-center gap-2"
          style={{ minHeight: '42px' }}
        >
          {loading ? (
            <>
              <Icon name="Loader2" size={16} className="animate-spin" />
              Signing in…
            </>
          ) : (
            <>
              <Icon name="LogIn" size={16} />
              Sign In to Admin Portal
            </>
          )}
        </button>
      </form>

    </div>
  );
}